import moment from 'moment';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';

import { TASK_LIST_PATH } from '@app/constants';
import { months } from '@app/pages/TasksList/constants/dates';
import type { NTaskListProvider } from '@app/providers/TaskListProvider/types';

const validTabs = ['registry', 'day', 'week', 'month'];
const isValidTab = (tab: string): tab is NTaskListProvider.Tab =>
  validTabs.includes(tab);

export const useTaskListRouting = () => {
  const { pathname } = useLocation();
  const isTaskList = useMemo(() => pathname === TASK_LIST_PATH, [pathname]);
  const [headerTitle, setHeaderTitle] = useState<string>('');
  const [searchParams, setSearchParams] = useSearchParams();
  const today = useMemo(() => moment(), []);
  const activeTab = useMemo(
    () => searchParams.get('tab') || '',
    [searchParams]
  );

  const validActiveTab = useMemo(
    () => (isValidTab(activeTab) ? activeTab : 'registry'),
    [activeTab]
  );
  const currentDate: NTaskListProvider.CurrentDate = useMemo(() => {
    const year = +(searchParams.get('year') || '');
    const month = +(searchParams.get('month') || '');
    const day = +(searchParams.get('day') || '');
    const week = +(searchParams.get('week') || '');

    return { year, month, day, week };
  }, [searchParams]);

  const getDefaultDate = useCallback(() => {
    const year = today.year().toString() || '';
    const month = (today.month() + 1).toString() || '';
    const day = today.date().toString() || '';
    const week = today.isoWeek().toString() || '';

    return { year, month, day, week };
  }, [today]);

  const onChangeTab = useCallback(
    (tab: NTaskListProvider.Tab) => {
      const { year, month, day, week } = getDefaultDate();

      const queryParams = {
        tab,
        year,
        ...(tab === 'month' ? { month } : {}),
        ...(tab === 'day' ? { month, day } : {}),
        ...(tab === 'week' ? { week } : {}),
      };

      setSearchParams({
        tab,
        ...(tab !== 'registry' ? { ...queryParams } : {}),
      });
    },
    [setSearchParams, getDefaultDate]
  );

  const handleValidQueryParams = useCallback(() => {
    if (!isTaskList) return;
    const { year, month, day, week } = currentDate;

    switch (activeTab) {
      case 'day':
        const isValidDailyDate = moment(
          `${year}-${month}-${day}`,
          'YYYY-MM-DD'
        ).isValid();
        if (!isValidDailyDate) {
          const { year, month, day } = getDefaultDate();
          setSearchParams({ tab: 'day', year, month, day });
        }

        break;
      case 'week':
        const isValidWeeklyDate = moment(
          `${year}-W${week}`,
          'YYYY-WW'
        ).isValid();
        if (!isValidWeeklyDate) {
          const { year, week } = getDefaultDate();
          setSearchParams({ tab: 'week', year, week });
        }
        break;
      case 'month':
        const isValidMonthlyDate = moment(
          `${year}-${month}`,
          'YYYY-MM'
        ).isValid();
        if (!isValidMonthlyDate) {
          const { year, month } = getDefaultDate();
          setSearchParams({ tab: 'month', year, month });
        }
        break;
      default:
        break;
    }
  }, [currentDate, activeTab, setSearchParams, getDefaultDate, isTaskList]);

  const handleDateTitle = useCallback(() => {
    const { year, month, week } = currentDate;

    switch (activeTab) {
      case 'day':
      case 'month':
        setHeaderTitle(`${months[Number(month)]} ${year}`);
        break;
      case 'week':
        const weekStart = moment(`${year}-${week}`, 'YYYY-W').startOf(
          'isoWeek'
        );
        const weekStartYear = weekStart.year();
        const weekStartMonth = weekStart.format('MM');
        const weekStartDay = weekStart.format('DD');

        const weekEnd = moment(`${year}-${week}`, 'YYYY-W').endOf('isoWeek');
        const weekEndYear = weekEnd.year();
        const weekEndMonth = weekEnd.format('MM');
        const weekEndDay = weekEnd.format('DD');

        setHeaderTitle(
          `${weekStartDay}.${weekStartMonth}.${weekStartYear} - ${weekEndDay}.${weekEndMonth}.${weekEndYear}`
        );
        break;
      default:
        setHeaderTitle('');
        break;
    }
  }, [currentDate, activeTab]);

  const toPrevDay = useCallback(() => {
    const { year, month, day } = currentDate;
    const newDate = moment(`${year}-${month}-${day}`, 'YYYY-MM-DD').subtract(
      1,
      'days'
    );
    setSearchParams({
      tab: 'day',
      year: newDate.year().toString(),
      month: (newDate.month() + 1).toString(),
      day: newDate.date().toString(),
    });
  }, [currentDate, setSearchParams]);

  const toNextDay = useCallback(() => {
    const { year, month, day } = currentDate;
    const newDate = moment(`${year}-${month}-${day}`, 'YYYY-MM-DD').add(
      1,
      'days'
    );
    setSearchParams({
      tab: 'day',
      year: newDate.year().toString(),
      month: (newDate.month() + 1).toString(),
      day: newDate.date().toString(),
    });
  }, [currentDate, setSearchParams]);

  const toPrevWeek = useCallback(() => {
    const { year, week } = currentDate;
    const weeksInYear = moment(`${year - 1}-${week}`, 'YYYY-W').weeksInYear();
    const newWeek =
      week === 1
        ? moment(`${year - 1}-${weeksInYear}`, 'YYYY-W')
        : moment(`${year}-${week}`, 'YYYY-W').subtract(1, 'week');
    setSearchParams({
      tab: 'week',
      year: newWeek.year().toString(),
      week: newWeek.isoWeek().toString(),
    });
  }, [currentDate, setSearchParams]);

  const toNextWeek = useCallback(() => {
    const { year, week } = currentDate;
    const weeksInYear = moment(`${year}-${week}`, 'YYYY-W').weeksInYear();
    const newWeek =
      week === weeksInYear
        ? moment(`${year + 1}`, 'YYYY')
        : moment(`${year}-${week}`, 'YYYY-W').add(1, 'week');

    setSearchParams({
      tab: 'week',
      year: newWeek.year().toString(),
      week: newWeek.isoWeek().toString(),
    });
  }, [currentDate, setSearchParams]);

  const toPrevMonth = useCallback(() => {
    const { year, month } = currentDate;
    const newMonth = month === 1 ? 12 : month - 1;
    const newYear = month === 1 ? year - 1 : year;

    setSearchParams({ tab: 'month', year: `${newYear}`, month: `${newMonth}` });
  }, [currentDate, setSearchParams]);

  const toNextMonth = useCallback(() => {
    const { year, month } = currentDate;
    const newMonth = month === 12 ? 1 : month + 1;
    const newYear = month === 12 ? year + 1 : year;

    setSearchParams({
      tab: 'month',
      year: newYear.toString(),
      month: newMonth.toString(),
    });
  }, [currentDate, setSearchParams]);

  const toPrevDate = useCallback(() => {
    switch (activeTab) {
      case 'day':
        toPrevDay();
        break;
      case 'week':
        toPrevWeek();
        break;
      case 'month':
        toPrevMonth();
        break;
      default:
        break;
    }
  }, [activeTab, toPrevDay, toPrevWeek, toPrevMonth]);

  const toNextDate = useCallback(() => {
    switch (activeTab) {
      case 'day':
        toNextDay();
        break;
      case 'week':
        toNextWeek();
        break;
      case 'month':
        toNextMonth();
        break;
      default:
        break;
    }
  }, [activeTab, toNextDay, toNextWeek, toNextMonth]);

  useEffect(() => {
    handleDateTitle();
  }, [handleDateTitle]);

  useEffect(() => {
    handleValidQueryParams();
  }, [handleValidQueryParams]);

  return {
    activeTab: validActiveTab,
    onChangeTab,
    headerTitle,
    toPrevDate,
    toNextDate,
    currentDate,
  };
};
